import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ProjectService from "../../services/Project.service";
import QuoteService from "../../services/Quote.service";

function formatTime(timeStr) {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10));
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function SectionHeader({ title }) {
    return (
        <Typography
            variant="caption"
            sx={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "text.secondary",
                display: "block",
                mb: 0.5,
            }}
        >
            {title}
        </Typography>
    );
}

function InfoRow({ icon, label, value, href }) {
    if (!value) return null;
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.25 }}>
            <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                >
                    {label}
                </Typography>
                {href ? (
                    <Typography
                        component="a"
                        href={href}
                        variant="body2"
                        sx={{ fontFamily: "Montserrat, sans-serif", display: "block", color: "primary.main", textDecoration: "none" }}
                    >
                        {value}
                    </Typography>
                ) : (
                    <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        {value}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

export default function ProjectDetailsDialog({ segment, onClose }) {
    const open = Boolean(segment);
    const isQuote = !segment?.project_detail?.id && !!segment?.quote_detail?.id;
    const [record, setRecord] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const projectId = segment?.project_detail?.id;
        const quoteId = segment?.quote_detail?.id;
        if (!projectId && !quoteId) {
            setRecord(null);
            setContacts([]);
            return;
        }
        setLoading(true);
        const service = projectId ? ProjectService : QuoteService;
        const id = projectId || quoteId;
        Promise.all([
            service.get(id),
            service.getContacts(id),
        ])
            .then(([res, contactsRes]) => {
                setRecord(res.data);
                setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data?.results || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [segment?.project_detail?.id, segment?.quote_detail?.id]);

    const detail = segment?.project_detail || segment?.quote_detail || {};
    const startTime = segment?.start_time ? formatTime(segment.start_time) : null;
    const location = segment?.start_location ? capitalize(segment.start_location) : null;

    const customerName = isQuote
        ? (record?.customers_detail || []).map((c) => c.name).filter(Boolean).join(", ")
        : record?.customer_detail?.name;

    const addr = record?.address_detail;
    const addressStr = addr
        ? [
              addr.address || addr.address_line1,
              addr.address_line2,
              addr.city,
              addr.state,
              addr.postal_code,
          ]
              .filter(Boolean)
              .join(", ")
        : null;

    const attachments = record?.attachments_detail || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: 3, border: 2, borderColor: segment?.is_testing ? "warning.main" : "transparent" } }}
        >
            <DialogTitle sx={{ pb: 0, pr: 1, fontFamily: "Montserrat, sans-serif" }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Chip
                            label={detail.number}
                            size="small"
                            color="primary"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, mb: 0.5 }}
                        />
                        <Typography
                            variant="h6"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, lineHeight: 1.2, fontSize: "1rem" }}
                        >
                            {detail.name}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                {segment?.is_testing && (
                    <Chip
                        label="Testing"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.75, fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                    />
                )}
            </DialogTitle>

            <Divider sx={{ mt: 1.5 }} />

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {/* Schedule info */}
                        <InfoRow icon={<AccessTimeIcon fontSize="small" />} label="Start Time" value={startTime} />
                        <InfoRow icon={<LocationOnIcon fontSize="small" />} label="Start Location" value={location} />

                        {/* Project info */}
                        <InfoRow icon={<BusinessIcon fontSize="small" />} label="Customer" value={customerName} />

                        {addressStr && (
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.25 }}>
                                <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}>
                                    <LocationOnIcon fontSize="small" />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                                    >
                                        Address
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif", flex: 1 }}>
                                            {addressStr}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressStr)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: "primary.main", flexShrink: 0 }}
                                            aria-label="Open in Google Maps"
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            </Stack>
                        )}

                        {/* Contacts */}
                        {contacts.length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <SectionHeader title="Contacts" />
                                {contacts.map((c) => (
                                    <Box key={c.id} sx={{ mb: 1.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
                                            <PersonIcon sx={{ fontSize: "0.95rem", color: "primary.main" }} />
                                            <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                                                {c.name}
                                            </Typography>
                                        </Stack>
                                        {c.job_title && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", display: "block", pl: 3 }}>
                                                {c.job_title}
                                            </Typography>
                                        )}
                                        {c.company?.name && (
                                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "Montserrat, sans-serif", display: "block", pl: 3 }}>
                                                {c.company.name}
                                            </Typography>
                                        )}
                                        {(c.phone || []).map((ph) => (
                                            ph.phone_number ? (
                                                <Stack key={ph.id} direction="row" spacing={0.75} alignItems="center" sx={{ pl: 3, mt: 0.25 }}>
                                                    <PhoneIcon sx={{ fontSize: "0.8rem", color: "text.secondary" }} />
                                                    <Typography
                                                        component="a"
                                                        href={`tel:${ph.phone_number}`}
                                                        variant="caption"
                                                        sx={{ fontFamily: "Montserrat, sans-serif", color: "primary.main", textDecoration: "none" }}
                                                    >
                                                        {ph.phone_number}
                                                    </Typography>
                                                </Stack>
                                            ) : null
                                        ))}
                                        {c.email && (
                                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pl: 3, mt: 0.25 }}>
                                                <EmailIcon sx={{ fontSize: "0.8rem", color: "text.secondary" }} />
                                                <Typography
                                                    component="a"
                                                    href={`mailto:${c.email}`}
                                                    variant="caption"
                                                    sx={{ fontFamily: "Montserrat, sans-serif", color: "primary.main", textDecoration: "none" }}
                                                >
                                                    {c.email}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                ))}
                            </>
                        )}

                        {/* Attachments */}
                        {attachments.length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <SectionHeader title="Files" />
                                <List dense disablePadding>
                                    {attachments.map((a) => (
                                        <ListItem
                                            key={a.id}
                                            disablePadding
                                            component="a"
                                            href={a.document_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: "primary.main", textDecoration: "none", borderRadius: 1, mb: 0.25, px: 0.5, py: 0.25, "&:hover": { bgcolor: "action.hover" } }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 30 }}>
                                                <AttachFileIcon sx={{ fontSize: "0.9rem", color: "primary.main" }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={a.title}
                                                primaryTypographyProps={{ variant: "body2", fontFamily: "Montserrat, sans-serif", fontSize: "0.8rem" }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
